import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { ClipboardCheck, Clock, FileQuestion, ArrowRight, BookOpen, Monitor } from "lucide-react";
import { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";

import { getToken } from "../services/auth";
import { getMyPurchases } from "../services/purchase";
import { getCourseExams } from "../services/exams";

export default function Exams() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [availableExams, setAvailableExams] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadExams = useCallback(async () => {
    const purchasesRes = await getMyPurchases();
    const examsByCourse = await Promise.all(
      purchasesRes.data.map(async (course) => {
        try {
          const examRes = await getCourseExams(course.id);
          return (examRes.data || []).map((exam) => ({
            ...exam,
            courseTitle: course.title,
          }));
        } catch (err) {
          return [];
        }
      })
    );
    setAvailableExams(examsByCourse.flat());
  }, []);

  useEffect(() => {
    const init = async () => {
      const token = getToken();
      if (!token) return;
      setUser(jwtDecode(token));
      try {
        await loadExams();
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [loadExams]);

  if (loading || !user) return (
    <div className="flex h-screen items-center justify-center dark:bg-slate-900">
      <div className="w-10 h-10 border-4 border-indigo-600/30 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="w-full">
      <Toaster position="top-center" />
      
      <main className="flex-1 p-6 lg:p-10 space-y-12 min-w-0">
        <header>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full text-xs font-black mb-6 uppercase tracking-widest">
              <ClipboardCheck size={14} fill="currentColor" /> Ready for assessment
            </div>
            <h1 className="text-4xl lg:text-5xl font-black text-slate-900 dark:text-white leading-tight tracking-tight">
              Test <span className="text-indigo-600">Series</span>
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-4 text-lg font-medium">
              Validate your knowledge with our premium mock tests and exams.
            </p>
          </motion.div>
        </header>

        {availableExams.length === 0 ? (
          <section className="p-20 bg-slate-50 dark:bg-slate-900 rounded-[3rem] text-center border-2 border-dashed border-slate-200 dark:border-slate-800">
            <FileQuestion className="mx-auto mb-6 text-slate-300 dark:text-slate-700" size={64} />
            <h2 className="text-2xl font-black text-slate-900 dark:text-white">No tests assigned</h2>
            <p className="text-slate-500 font-medium mt-4">Once exams are published for your purchased courses, they'll appear here.</p>
          </section>
        ) : (
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {availableExams.map((exam, i) => (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5 }}
                key={exam.id} 
                className="group p-8 bg-white dark:bg-slate-900 rounded-[3.5rem] border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-2xl transition-all relative overflow-hidden"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="p-4 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-3xl group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                    <BookOpen size={24} />
                  </div>
                  <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 text-[10px] font-black uppercase tracking-widest">
                    <Clock size={12} /> {exam.duration_minutes}m
                  </div>
                </div>

                <div className="min-w-0 mb-8">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 truncate">{exam.courseTitle}</p>
                  <h3 className="text-xl font-black text-slate-900 dark:text-white line-clamp-2 min-h-[3.5rem] group-hover:text-indigo-600 transition-colors">{exam.title}</h3>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-10">
                  <div className="rounded-3xl bg-slate-50 dark:bg-slate-800 p-5 text-center">
                    <div className="text-xl font-black text-slate-900 dark:text-white">{exam.questions.length}</div>
                    <div className="text-[10px] uppercase font-black text-slate-400 tracking-tighter mt-1">Questions</div>
                  </div>
                  <div className="rounded-3xl bg-slate-50 dark:bg-slate-800 p-5 text-center">
                    <div className="text-xl font-black text-emerald-600 dark:text-emerald-400">{exam.passing_score}%</div>
                    <div className="text-[10px] uppercase font-black text-slate-400 tracking-tighter mt-1">Passing Mark</div>
                  </div>
                </div>

                <button
                  onClick={() => navigate(`/exam/${exam.course_id}?examId=${exam.id}`)}
                  className="w-full py-5 bg-slate-900 dark:bg-indigo-600 text-white font-black rounded-[2rem] shadow-xl shadow-slate-200 dark:shadow-none hover:bg-indigo-600 transition-all active:scale-[0.98] flex items-center justify-center gap-3 group/btn"
                >
                  {localStorage.getItem(`exam_state_${exam.id}`) ? "Resume Assessment" : "Start Assessment"}
                  <ArrowRight size={20} className="group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </motion.div>
            ))}
          </section>
        )}
      </main>
    </div>
  );
}

